import {
  SquaresFour, Folder, Truck, Bell, Gear, ClipboardText, ChartBar, Heartbeat, GraduationCap, Bookmark, ChatCircle, ShieldCheck,
  Lightning, Trophy, CalendarBlank, Robot, Briefcase, PenNib, Warning, ArrowsLeftRight, Buildings, Receipt, FileText, Book, Palette,
  Globe, EnvelopeSimple, Database, Scales, UsersThree, CreditCard, TrendUp, Sparkle, ChartLineUp, Compass, TreeStructure,
  Megaphone, HandPalm, Gauge, Star, MapPin, Users, Clock, MapTrifold, WhatsappLogo, Monitor, Kanban, CalendarCheck, ChatText, Wrench, Package, CurrencyGbp,
  Tray, Wallet, FirstAid, ShieldWarning, ChalkboardTeacher, Certificate, Timer, Scroll, Flask, HardHat, Bank, Calculator, ArrowsDownUp, Books, Money, Camera, Car,
  Pulse, Storefront, HouseLine, Lifebuoy, Hammer, Handshake, PuzzlePiece,
} from "@phosphor-icons/react";
import { BESPOKE_ICONS } from "../../lib/bespoke";

const ADMIN3 = ["super_admin", "admin", "project_manager"];
const ADMIN2 = ["super_admin", "admin"];
const HS = [...ADMIN3, "team_member", "hs_manager"];
const HR_FULL = ["super_admin", "hr_manager"];
const HR_OPS = ["admin", "project_manager"];

/**
 * Sidebar structure. A section is either
 *   • flat   — `{ id, items }` (no label): plain links, or
 *   • group  — `{ id, label, icon, items, to?, roles? }`: a collapsible heading. `to` makes the heading itself a link
 *              (Projects → /projects, Marketplace → /marketplace); `flat: true` keeps a labelled section un-collapsible
 *              (client / operative portals keep their simple look).
 * Items: `{ to, label, icon, roles, feature?, exact?, noQuery?, activePrefix?, platformAdminOnly?, opsDirectorOnly?, mainClientOnly? }`;
 * `{ heading }` inside a group is a tiny divider label (Revenue & Growth › Insights).
 */
export const NAV_SECTIONS = [
  // ── Client portal ─────────────────────────────────────────────────────────
  {
    id: "client_portal",
    label: "Client Portal",
    flat: true,
    roles: ["client"],
    items: [
      { to: "/portal", label: "Dashboard", icon: SquaresFour, roles: ["client"], exact: true },
      { to: "/portal/requests", label: "Job Requests", icon: ClipboardText, roles: ["client"] },
      { feature: "emergency_jobs", to: "/portal/emergencies", label: "Emergencies", icon: Lightning, roles: ["client"] },
      { to: "/portal/jobs", label: "Jobs", icon: Briefcase, roles: ["client"] },
      { to: "/portal/book", label: "Book a visit", icon: CalendarCheck, roles: ["client"] },
      { to: "/portal/projects", label: "Projects", icon: Kanban, roles: ["client"] },
      { feature: "contracts", to: "/portal/contracts", label: "Contracts", icon: CalendarCheck, roles: ["client"] },
      { to: "/portal/sub-clients", label: "Sub-clients", icon: TreeStructure, roles: ["client"], mainClientOnly: true },
      { to: "/portal/calendar", label: "Calendar", icon: CalendarBlank, roles: ["client"] },
      { to: "/portal/quotes", label: "Quotes", icon: Receipt, roles: ["client"] },
      { to: "/portal/invoices", label: "Invoices", icon: CreditCard, roles: ["client"] },
      { to: "/portal/budgets", label: "Budgets", icon: Wallet, roles: ["client"] },
      { to: "/portal/files", label: "File Hub", icon: Folder, roles: ["client"] },
      { to: "/portal/messages", label: "Messages", icon: ChatCircle, roles: ["client"] },
    ],
  },
  {
    id: "client_account",
    label: "Account",
    flat: true,
    roles: ["client"],
    items: [
      { to: "/portal/properties", label: "Properties", icon: Buildings, roles: ["client"] },
      { to: "/portal/assets", label: "Assets", icon: Wrench, roles: ["client"] },
      { to: "/portal/assets/capex", label: "Capital forecast", icon: ChartLineUp, roles: ["client"] },
      { to: "/portal/compliance", label: "Compliance", icon: ShieldCheck, roles: ["client"] },
      { to: "/portal/safety", label: "Health & safety", icon: FirstAid, roles: ["client"] },
      { to: "/portal/ppm", label: "Planned maintenance", icon: CalendarCheck, roles: ["client"] },
      { to: "/portal/reports", label: "Reports", icon: ChartBar, roles: ["client"] },
      { to: "/portal/team", label: "Team", icon: Users, roles: ["client"] },
      { to: "/notifications", label: "Notifications", icon: Bell, roles: ["client"] },
      { to: "/portal/settings", label: "Settings", icon: Gear, roles: ["client"] },
      { to: "/portal/help", label: "Help", icon: Book, roles: ["client"] },
    ],
  },

  // ── Office (admin / PM) menu — order agreed with the user ─────────────────
  {
    id: "main",
    items: [
      { to: "/", label: "Dashboard", icon: SquaresFour, roles: [...ADMIN3, "team_member", "vendor", "stakeholder"] },
      { feature: "supplier_portal", to: "/supplier-portal", label: "My Portal", icon: Briefcase, roles: ["vendor"] },
      { to: "/job-requests", label: "Job Requests", icon: ClipboardText, roles: ADMIN3 },
      { to: "/jobs", label: "Jobs", icon: Briefcase, roles: [...ADMIN3, "vendor"] },
      { feature: "emergency_jobs", to: "/emergency-jobs", label: "Emergency Jobs", icon: Lightning, roles: [...ADMIN3, "vendor"] },
    ],
  },
  {
    id: "projects",
    label: "Projects",
    icon: Kanban,
    to: "/projects",
    roles: [...ADMIN3, "team_member", "stakeholder"],
    items: [
      { to: "/timeline", label: "Timeline", icon: CalendarBlank, roles: ADMIN3 },
    ],
  },
  {
    id: "commercial",
    items: [
      { feature: "contracts", to: "/contracts", label: "Contracts", icon: CalendarCheck, roles: ADMIN3 },
      { to: "/quotes", label: "Quotes", icon: Receipt, roles: ADMIN3 },
      { to: "/work-orders", label: "Works Orders", icon: ClipboardText, roles: [...ADMIN3, "vendor"] },
      { to: "/budgets", label: "Budgets", icon: Wallet, roles: ADMIN3 },
      { to: "/invoices", label: "Invoices", icon: Receipt, roles: ADMIN3 },
    ],
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: CalendarBlank,
    items: [
      { to: "/calendar", label: "Schedule", icon: CalendarBlank, roles: ADMIN3 },
      { to: "/schedule", label: "Team Schedule", icon: Users, roles: ADMIN3 },
    ],
  },
  {
    id: "comms",
    label: "Communications",
    icon: ChatCircle,
    items: [
      { to: "/inbox", label: "Inbox", icon: Tray, roles: ADMIN3 },
      { feature: "whatsapp", to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, roles: [...ADMIN3, "team_member"] },
      { to: "/text-messages", label: "Text Messages", icon: ChatText, roles: ADMIN3 },
      { to: "/notifications", label: "Notifications", icon: Bell, roles: [...ADMIN3, "team_member", "vendor", "stakeholder", "hs_manager"] },
    ],
  },
  {
    id: "files",
    items: [
      { to: "/files", label: "File Hub", icon: Folder, roles: ADMIN3 },
    ],
  },
  {
    id: "clients",
    label: "Clients",
    icon: Buildings,
    items: [
      { to: "/clients", exact: true, noQuery: true, activePrefix: "/clients/", label: "Clients", icon: Buildings, roles: ADMIN3 },
      { to: "/clients?kind=sub", label: "Sub Clients", icon: TreeStructure, roles: ADMIN3 },
      { to: "/properties", label: "Properties", icon: HouseLine, roles: ADMIN3 },
      { to: "/tenant-report-forms", label: "Tenant Report Forms", icon: ClipboardText, roles: ADMIN3 },
    ],
  },
  {
    id: "in_house_admin",
    label: "In-House Team",
    icon: Users,
    feature: "in_house_team",
    items: [
      { to: "/settings/in-house-team", label: "Team", icon: Users, roles: ADMIN3 },
      { to: "/dispatch-board", label: "Dispatch Board", icon: Kanban, roles: ADMIN3 },
      { to: "/live-team-map", label: "Live Team Map", icon: MapTrifold, roles: ADMIN3 },
      { to: "/in-house/tracking", label: "Tracking", icon: Compass, roles: ADMIN3 },
      { feature: "route_planner", to: "/in-house/mileage-log", label: "Mileage Log", icon: Car, roles: ADMIN3 },
      { to: "/stock", label: "Stock & Parts", icon: Package, roles: ADMIN3 },
      { to: "/settings/timesheets", label: "Timesheets", icon: Clock, roles: ADMIN3 },
      { to: "/settings/expenses", label: "Expenses", icon: Receipt, roles: ADMIN3 },
      { to: "/in-house/pos", label: "Purchase Orders", icon: ClipboardText, roles: ADMIN3 },
    ],
  },
  {
    id: "suppliers",
    label: "Suppliers",
    icon: Truck,
    feature: "suppliers",
    items: [
      { to: "/vendors", label: "Suppliers", icon: Truck, roles: ADMIN3 },
      { to: "/suppliers/nearby", label: "Nearby", icon: MapPin, roles: ADMIN3 },
      { to: "/supplier-compliance", label: "Compliance", icon: ShieldCheck, roles: ADMIN3 },
      { feature: "advanced_analytics", to: "/supplier-analytics", label: "Analytics", icon: ChartBar, roles: ADMIN3 },
      { to: "/supplier-compare", label: "Compare", icon: ArrowsLeftRight, roles: ADMIN3 },
      { to: "/supplier-onboarding", label: "Onboarding", icon: Lightning, roles: ADMIN2 },
      { to: "/supplier-outreach", label: "Outreach", icon: Megaphone, roles: ADMIN2 },
      { to: "/vendor-scorecard", label: "Scorecard", icon: Trophy, roles: ADMIN3 },
      { feature: "in_house_team", to: "/operative-scorecard", label: "Team Scorecard", icon: Trophy, roles: [...ADMIN3, "team_member"] },
    ],
  },
  {
    id: "marketplace",
    label: "Marketplace",
    icon: Storefront,
    to: "/marketplace",
    feature: "marketplace",
    roles: ADMIN3,
    exact: true,
    noQuery: true,
    activePrefix: "/marketplace/contractors",
    items: [
      { feature: "marketplace", to: "/marketplace/requests", label: "My Requests", icon: Tray, roles: ADMIN3 },
      { feature: "marketplace", to: "/marketplace?favourites=1", label: "Favourites", icon: Star, roles: ADMIN3 },
    ],
  },
  {
    id: "cmms",
    label: "CMMS",
    icon: Wrench,
    items: [
      { to: "/assets", label: "Assets", icon: Wrench, roles: [...ADMIN3, "team_member"] },
      { to: "/compliance", label: "Building compliance", icon: ShieldCheck, roles: [...ADMIN3, "team_member"] },
      { to: "/ppm", label: "PPM planner", icon: CalendarCheck, roles: [...ADMIN3, "team_member"] },
      { feature: "ai_drafting", to: "/predictive-maintenance", label: "Predictive Maintenance", icon: Sparkle, roles: ADMIN3 },
    ],
  },
  {
    id: "hr",
    label: "HR & People",
    icon: UsersThree,
    feature: "hr",
    items: [
      { to: "/hr", exact: true, noQuery: true, label: "HR Overview", icon: UsersThree, roles: HR_FULL },
      { to: "/hr", exact: true, noQuery: true, label: "HR Overview", icon: UsersThree, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr?tab=people", activePrefix: "/hr/people", label: "People", icon: Users, roles: HR_FULL },
      { to: "/hr?tab=people", activePrefix: "/hr/people", label: "People", icon: Users, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr?tab=attendance", label: "Attendance", icon: Timer, roles: HR_FULL },
      { to: "/hr?tab=attendance", label: "Attendance", icon: Timer, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr?tab=absence", label: "Absence & Holidays", icon: CalendarCheck, roles: HR_FULL },
      { to: "/hr?tab=absence", label: "Absence & Holidays", icon: CalendarCheck, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr?tab=recruitment", label: "Recruitment", icon: Briefcase, roles: HR_FULL },
      { to: "/hr?tab=recruitment", label: "Recruitment", icon: Briefcase, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr?tab=settings", label: "HR Settings", icon: Gear, roles: HR_FULL },
      { to: "/hr?tab=settings", label: "HR Settings", icon: Gear, roles: HR_OPS, opsDirectorOnly: true },
      { to: "/hr/clock", label: "Clock In / Out", icon: Timer, roles: ["super_admin", "admin", "project_manager", "hs_manager", "hr_manager", "team_member", "stakeholder"] },
      { to: "/hr/me", label: "My HR", icon: Users, roles: ["super_admin", "admin", "project_manager", "hs_manager", "hr_manager", "team_member", "stakeholder"] },
    ],
  },
  {
    id: "health_safety",
    label: "Health & Safety",
    icon: FirstAid,
    items: [
      { to: "/health-safety", exact: true, noQuery: true, label: "H&S Overview", icon: FirstAid, roles: HS },
      { to: "/health-safety?tab=rams", activePrefix: "/health-safety/rams", label: "RAMS", icon: ClipboardText, roles: HS },
      { to: "/health-safety?tab=rams&kind=ra", label: "Risk Assessments", icon: Warning, roles: HS },
      { to: "/health-safety?tab=library&group=policies", label: "H&S Policies", icon: Scroll, roles: HS },
      { to: "/health-safety?tab=library&group=coshh", label: "COSHH", icon: Flask, roles: HS },
      { to: "/health-safety?tab=library&group=cdm", label: "CDM 2015", icon: HardHat, roles: HS },
      { to: "/health-safety?tab=permits", label: "Permits to Work", icon: ShieldWarning, roles: HS },
      { to: "/health-safety?tab=toolbox", label: "Toolbox Talks", icon: ChalkboardTeacher, roles: HS },
      { to: "/health-safety?tab=lone-worker", label: "Lone Worker", icon: Timer, roles: HS },
      { to: "/health-safety?tab=training", activePrefix: "/health-safety/training", label: "Training", icon: GraduationCap, roles: HS },
      { to: "/health-safety?tab=competency", label: "Competency Matrix", icon: Certificate, roles: HS },
      { feature: "hs_library", to: "/health-safety?tab=template-library", label: "Template Library", icon: Sparkle, roles: HS },
    ],
  },
  {
    id: "accounts",
    label: "Accounting",
    icon: Calculator,
    feature: "accounts",
    items: [
      { to: "/accounts", exact: true, label: "Overview", icon: Calculator, roles: ADMIN2 },
      { to: "/accounts/banking", label: "Banking", icon: Bank, roles: ADMIN2 },
      { to: "/accounts/transactions", label: "Transactions", icon: ArrowsDownUp, roles: ADMIN2 },
      { to: "/payments/card", label: "Card Payments", icon: CreditCard, roles: ADMIN2 },
      { to: "/accounts/reports", label: "Reports", icon: Books, roles: ADMIN2 },
      { to: "/accounts/vat", label: "VAT returns", icon: Scales, roles: ADMIN2 },
      { feature: "hr", to: "/accounts/payroll", label: "Payroll", icon: Money, roles: ADMIN2 },
      { to: "/accounts/receipts", label: "Receipts", icon: Receipt, roles: ADMIN2 },
      { to: "/accounts/settings", label: "Accounts settings", icon: Gear, roles: ADMIN2 },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: Lifebuoy,
    items: [
      { feature: "ai_drafting", to: "/ai-assistant", label: "AI Assistant", icon: Robot, roles: ADMIN3 },
      { to: "/knowledge-base", label: "Knowledge Base", icon: Book, roles: [...ADMIN3, "team_member", "hs_manager"] },
    ],
  },
  {
    id: "operational",
    label: "Operational Info",
    icon: Pulse,
    items: [
      { to: "/portfolio-health", label: "Portfolio Health", icon: Heartbeat, roles: [...ADMIN3, "stakeholder"] },
      { to: "/slas", label: "SLAs", icon: Gauge, roles: ADMIN3 },
      { feature: "compliance_hub", to: "/compliance-hub", label: "Compliance", icon: ShieldCheck, roles: ADMIN3 },
      { to: "/document-expiry", label: "Compliance Calendar", icon: CalendarBlank, roles: ADMIN3 },
      { to: "/activity-log", label: "Activity Log", icon: Lightning, roles: ADMIN3 },
      { to: "/reports", exact: true, label: "Reports", icon: ChartBar, roles: ADMIN3 },
      { to: "/staff-performance", label: "Staff Performance", icon: ChartLineUp, roles: ["super_admin"] },
      { to: "/staff-monitoring", label: "Staff Monitoring", icon: Monitor, roles: ADMIN3, opsDirectorOnly: true },
    ],
  },
  {
    id: "revenue_growth",
    label: "Revenue & Growth",
    icon: TrendUp,
    items: [
      { to: "/platform/usage-alerts", label: "Usage Alerts", icon: Gauge, roles: ADMIN2 },
      { to: "/platform/downgrade", label: "Manage Subscription", icon: HandPalm, roles: ADMIN2 },
      { heading: "Insights" },
      { feature: "advanced_analytics", to: "/tenant-analytics", label: "Analytics", icon: ChartBar, roles: ADMIN3 },
      { feature: "advanced_analytics", to: "/reports/profit-loss", label: "Profit & Loss", icon: TrendUp, roles: ADMIN2 },
      { feature: "advanced_analytics", to: "/reports/supplier-scorecard", label: "Supplier Scorecard", icon: Star, roles: ADMIN3 },
      { feature: "advanced_analytics", to: "/reports/client-spend", label: "Client Spend", icon: UsersThree, roles: ADMIN3 },
    ],
  },
  {
    id: "system",
    label: "Settings",
    icon: Gear,
    items: [
      { to: "/job-forms", label: "Job Forms", icon: PenNib, roles: ADMIN3 },
      { to: "/settings/checklists", label: "Checklists", icon: PenNib, roles: ADMIN3 },
      { to: "/settings/price-books", label: "Price Books", icon: CurrencyGbp, roles: ADMIN3 },
      { to: "/templates", label: "Templates", icon: Bookmark, roles: ADMIN3 },
      { to: "/document-templates", label: "Doc Templates", icon: FileText, roles: ADMIN3 },
      { to: "/platform/billing", label: "Billing", icon: Receipt, roles: ADMIN2 },
      { to: "/platform/branding", label: "Branding", icon: Palette, roles: ADMIN2 },
      { feature: "webhooks", to: "/platform/webhooks", label: "Webhooks", icon: Globe, roles: ADMIN2 },
      { to: "/platform/email-templates", label: "Email Templates", icon: EnvelopeSimple, roles: ADMIN2 },
      { to: "/platform/data-management", label: "Data Management", icon: Database, roles: ADMIN2 },
      { to: "/platform/audit-export", label: "Audit Export", icon: Scales, roles: ADMIN2 },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    icon: Hammer,
    platformAdminOnly: true,
    items: [
      { to: "/platform/tenants", label: "SaaS Tenants", icon: Buildings, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/subscription-plans", label: "Subscription Plans", icon: CreditCard, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/trial-conversions", label: "Trial Conversions", icon: TrendUp, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/api-usage", label: "API Usage", icon: ChartLineUp, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/revenue", label: "Revenue", icon: ChartBar, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/resellers", label: "Resellers", icon: Handshake, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/bespoke", label: "Bespoke Modules", icon: PuzzlePiece, roles: ["super_admin"], platformAdminOnly: true, activePrefix: "/platform/bespoke" },
      { to: "/platform/cohort-analysis", label: "Cohort Analysis", icon: ChartLineUp, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/tenant-comparison", label: "SaaS Tenant Comparison", icon: UsersThree, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/scheduler-health", label: "Scheduler Health", icon: Pulse, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/system-status", label: "System Status", icon: Heartbeat, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/email-health", label: "E-mail Health", icon: EnvelopeSimple, roles: ["super_admin"], platformAdminOnly: true },
      { to: "/platform/hs-library", label: "H&S Library", icon: FirstAid, roles: ["super_admin"], platformAdminOnly: true, activePrefix: "/platform/hs-library" },
    ],
  },

  // ── Supplier (vendor) + operative portals ─────────────────────────────────
  {
    id: "vendor_portal",
    items: [
      { to: "/vendors", label: "Documents", icon: ShieldCheck, roles: ["vendor"] },
    ],
  },
  {
    id: "in_house_portal",
    label: "My Portal",
    flat: true,
    roles: ["in_house", "vendor_operative"],
    items: [
      { to: "/my-work", exact: true, label: "My Work", icon: Briefcase, roles: ["in_house", "vendor_operative"] },
      { to: "/my-work#schedule", label: "Schedule", icon: CalendarBlank, roles: ["in_house", "vendor_operative"] },
      { feature: "route_planner", to: "/my-work/route-planner", label: "Route Planner", icon: MapTrifold, roles: ["in_house", "vendor_operative"] },
      { feature: "compliance_hub", to: "/compliance-hub", label: "Compliance", icon: ShieldCheck, roles: ["in_house"] },
      { to: "/messages", label: "Messages", icon: ChatCircle, roles: ["in_house"] },
      { feature: "whatsapp", to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, roles: ["in_house"] },
      { feature: "accounts", to: "/my-work/receipts", label: "Receipts", icon: Camera, roles: ["in_house"] },
      { feature: "hr", to: "/my-work/payslips", label: "Payslips", icon: Money, roles: ["in_house"] },
      { feature: "hr", to: "/my-work/leave", label: "Leave", icon: CalendarCheck, roles: ["in_house"] },
      { feature: "hr", to: "/my-work/hr", label: "My HR", icon: Users, roles: ["in_house"] },
      { feature: "hr", to: "/hr/clock", label: "Clock In / Out", icon: Timer, roles: ["in_house"] },
      { to: "/my-work/mileage", label: "Mileage", icon: Car, roles: ["in_house"] },
      { to: "/notifications", label: "Alerts", icon: Bell, roles: ["in_house", "vendor_operative"] },
      { to: "/my-work/help", label: "Help", icon: Book, roles: ["in_house", "vendor_operative"] },
    ],
  },
];

// Flat list for the mobile bottom bar
export const MOBILE_NAV = [
  { to: "/portal", label: "Home", icon: SquaresFour, roles: ["client"] },
  { to: "/portal/requests", label: "Requests", icon: ClipboardText, roles: ["client"] },
  { to: "/portal/jobs", label: "Jobs", icon: Briefcase, roles: ["client"] },
  { to: "/portal/messages", label: "Messages", icon: ChatCircle, roles: ["client"] },
  { to: "/notifications", label: "Alerts", icon: Bell, roles: ["client"] },
  { to: "/", label: "Dashboard", icon: SquaresFour, roles: [...ADMIN3, "team_member", "vendor", "stakeholder"] },
  { to: "/my-work", label: "My Work", icon: Briefcase, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work#schedule", label: "Schedule", icon: CalendarBlank, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work/route-planner", label: "Route", icon: MapTrifold, roles: ["in_house", "vendor_operative"] },
  { to: "/my-work/help", label: "Help", icon: Book, roles: ["in_house", "vendor_operative"] },
  { to: "/jobs", label: "Jobs", icon: Briefcase, roles: [...ADMIN3, "vendor"] },
  { to: "/messages", label: "Messages", icon: ChatCircle, roles: [...ADMIN3, "vendor", "in_house"] },
  { to: "/notifications", label: "Alerts", icon: Bell, roles: [...ADMIN3, "team_member", "vendor", "stakeholder", "in_house", "vendor_operative"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ADMIN2 },
];

export const navSlug = (label) => label.toLowerCase().replace(/[\s&/]+/g, "-").replace(/-+/g, "-");

// Bespoke modules switched on for the signed-in workspace (from /auth/me) become their own "Bespoke" group, placed before Platform
export function withBespoke(sections, user) {
  const mods = Array.isArray(user?.bespoke_modules) ? user.bespoke_modules : [];
  if (!mods.length) return sections;
  const group = { id: "bespoke", label: "Bespoke", icon: PuzzlePiece, items: mods.map((m) => ({ to: m.path, label: m.name, icon: BESPOKE_ICONS[m.icon] || PuzzlePiece, roles: m.roles || [], activePrefix: m.path })) };
  const idx = sections.findIndex((s) => s.id === "platform");
  return idx === -1 ? [...sections, group] : [...sections.slice(0, idx), group, ...sections.slice(idx)];
}
